import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {BaseModal} from 'components/modals';
import {Button} from '@/components/ui';
import Icon from '@/components/icons/Icon';
import {formatLuceneQuery} from '@/views/storyboard/chart_settings/formatLuceneQuery';
import ChartQuerySyntaxFeedback from '@/views/storyboard/chart_settings/ChartQuerySyntaxFeedback';
import {validateLuceneQuerySyntax} from '@/views/storyboard/utils/validateLuceneQuerySyntax';
import QuerySimilarWordsPopover from '@/views/search/QuerySimilarWordsPopover';
import {useQuerySimilarWordsPopover} from '@/views/search/hooks/useQuerySimilarWordsPopover';
import i18n from 'i18n';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    value: string;
    onApply: (value: string) => void;
}

const DEFAULT_EDITOR_HEIGHT = 350;
const MIN_EDITOR_HEIGHT = 220;
const MAX_EDITOR_HEIGHT_RATIO = 0.72;
const LARGE_EDITOR_HEIGHT_RATIO = 0.62;
const LINE_HEIGHT_PX = 21;

const getInitialEditorHeight = (query: string): number => {
    const lineCount = Math.max(query.split('\n').length, 1);
    const estimatedHeight = lineCount * LINE_HEIGHT_PX + 48;
    const maxHeight = Math.floor(window.innerHeight * MAX_EDITOR_HEIGHT_RATIO);

    return Math.min(Math.max(estimatedHeight, DEFAULT_EDITOR_HEIGHT), maxHeight);
};

const getLargeEditorHeight = (): number => Math.floor(window.innerHeight * LARGE_EDITOR_HEIGHT_RATIO);

const clampEditorHeight = (height: number): number => {
    const maxHeight = Math.floor(window.innerHeight * MAX_EDITOR_HEIGHT_RATIO);

    return Math.min(Math.max(height, MIN_EDITOR_HEIGHT), maxHeight);
};

const ChartQueryExpandModal = React.memo<Props>(({
    isOpen,
    onClose,
    value,
    onApply,
}) => {
    const [localValue, setLocalValue] = useState(value);
    const [editorHeight, setEditorHeight] = useState(DEFAULT_EDITOR_HEIGHT);
    const [isLargeModal, setIsLargeModal] = useState(false);

    const hasSyntaxIssues = useMemo(
        () => validateLuceneQuerySyntax(localValue).issues.length > 0,
        [localValue],
    );

    const appendWord = useCallback((word: string) => {
        setLocalValue((current) => {
            if (word.split(' ').length > 1) {
                return `${current} "${word}"`;
            }

            return `${current} ${word}`;
        });
    }, []);

    const {
        setInputElement,
        popoverTarget,
        activeWord,
        similarWords,
        loadingSimilarWords,
        showPopover,
        existingWords,
        handleInputClick,
        handlePopoverHide,
        handleAddWord,
        handleQueryInputChange,
    } = useQuerySimilarWordsPopover({
        queryValue: localValue,
        onAppendWord: appendWord,
    });

    useEffect(() => {
        if (!isOpen) {
            handleQueryInputChange();
        }
    }, [isOpen, handleQueryInputChange]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setLocalValue(value);
        setIsLargeModal(false);
        setEditorHeight(getInitialEditorHeight(value));
    }, [isOpen, value]);

    const handleFormat = useCallback(() => {
        setLocalValue(prev => formatLuceneQuery(prev));
    }, []);

    const handleDone = useCallback(() => {
        onApply(localValue);
        onClose();
    }, [localValue, onApply, onClose]);

    const toggleLargeModal = useCallback(() => {
        setIsLargeModal((current) => {
            const next = !current;

            setEditorHeight(next ? getLargeEditorHeight() : getInitialEditorHeight(localValue));

            return next;
        });
    }, [localValue]);

    const handleResizeStart = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        const startY = event.clientY;
        const startHeight = editorHeight;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            setEditorHeight(clampEditorHeight(startHeight + moveEvent.clientY - startY));
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [editorHeight]);

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title={i18n.t('chart_settings_edit_search_query.text')}
            size={isLargeModal ? 'xl' : 'lg'}
            backdropClassName="chart-query-expand-modal-backdrop"
            className="chart-query-expand-modal-layer"
            dialogClassName={isLargeModal ? 'chart-query-expand-modal-dialog--large' : ''}
            headerActions={(
                <button
                    type="button"
                    className="modal-header__icon-btn"
                    onClick={toggleLargeModal}
                    aria-label={isLargeModal
                        ? i18n.t('chart_settings_query_modal_default_size.text')
                        : i18n.t('chart_settings_query_modal_larger.text')}
                    title={isLargeModal
                        ? i18n.t('chart_settings_query_modal_default_size.text')
                        : i18n.t('chart_settings_query_modal_larger.text')}
                >
                    <Icon
                        name={isLargeModal ? 'mdi-unfold-less-horizontal' : 'mdi-arrow-expand-all'}
                        size="16px"
                    />
                </button>
            )}
        >
            <QuerySimilarWordsPopover
                target={popoverTarget}
                show={isOpen && showPopover}
                activeWord={activeWord ?? ''}
                similarWords={similarWords}
                loading={loadingSimilarWords}
                existingWords={existingWords}
                onHide={handlePopoverHide}
                onAddWord={handleAddWord}
            />

            <div className="chart-query-expand-modal">
                <div
                    className={`chart-query-expand-modal__editor${hasSyntaxIssues ? ' chart-query-expand-modal__editor--invalid' : ''}`}
                    style={{height: editorHeight}}
                >
                    <textarea
                        ref={setInputElement}
                        className="chart-query-expand-modal__textarea"
                        value={localValue}
                        onClick={handleInputClick}
                        onChange={(e) => {
                            handleQueryInputChange();
                            setLocalValue(e.target.value);
                        }}
                        spellCheck={false}
                        autoFocus
                    />
                    <button
                        type="button"
                        className="chart-query-expand-modal__resize-handle"
                        onMouseDown={handleResizeStart}
                        aria-label={i18n.t('chart_settings_query_modal_resize.text')}
                    >
                        <Icon name="mdi-unfold-more-horizontal" size="14px" />
                    </button>
                </div>

                <ChartQuerySyntaxFeedback
                    query={localValue}
                    className="chart-query-expand-modal__syntax-issues"
                />

                <div className="chart-query-expand-modal__footer">
                    <span className="chart-query-expand-modal__char-count">
                        {i18n.t('chart_settings_query_chars.text', {count: localValue.length})}
                    </span>
                    <div className="chart-query-expand-modal__spacer" />
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        type="button"
                        onClick={handleFormat}
                    >
                        {i18n.t('cs_format_query.text')}
                    </Button>
                    <Button
                        variant="info"
                        size="sm"
                        type="button"
                        onClick={handleDone}
                    >
                        {i18n.t('chart_settings_done.text')}
                    </Button>
                </div>
            </div>
        </BaseModal>
    );
});

export default ChartQueryExpandModal;
